// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * The Jira ticket a change was done under (design doc §8.6): optional on every form that
 * records a change, and a link on every record of one.
 *
 * Kept as the address the operator pasted rather than a ticket key resolved against a
 * configured site: it opens whatever Jira the team uses, with nothing to set up. The
 * server takes only an http(s) address, as the form does, since it is rendered as a link.
 */

import { useTranslation } from "react-i18next";

export const JIRA_URL_MAX_LENGTH = 500;

/** Whether a typed value can be sent: blank (none) or an http(s) address. */
export function isValidJiraUrl(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return true;
  if (trimmed.length > JIRA_URL_MAX_LENGTH) return false;
  try {
    const url = new URL(trimmed);
    return (url.protocol === "http:" || url.protocol === "https:") && Boolean(url.host);
  } catch {
    return false;
  }
}

/** What the link reads as: the ticket key when the address carries one (`SUP-123`). */
export function jiraLabel(url: string): string | null {
  const keys = url.match(/[A-Z][A-Z0-9_]+-\d+/g);
  return keys ? (keys[keys.length - 1] ?? null) : null;
}

export function JiraLink({ url }: { url: string | null | undefined }) {
  const { t } = useTranslation();
  if (!url) return null;
  return (
    <a
      className="jira-link"
      href={url}
      target="_blank"
      rel="noreferrer"
      title={url}
      // Inside clickable rows: opening the ticket must not also open or fold the row.
      onClick={(click) => click.stopPropagation()}
    >
      {jiraLabel(url) ?? t("maintenance.jiraOpen")}
      <span aria-hidden="true">↗</span>
    </a>
  );
}
